// Forked into ../redesign/Results/GroupAssets.tsx — keep in sync until phase_0 cleanup.
import React from 'react';
import { useTranslate } from '@bynder/localization';
import type { AssetsSearchResults } from '../types';
import { getMatchExplanation, onItemClick } from '../helpers/spotlightAssetActions';
import { useSpotlightCapabilities } from '../hooks/useSpotlightCapabilities';
import {
	ClassicItem,
	ClassicNavigableHeadingButton,
	StyledBadge,
	StyledInfoDiv,
	StyledItemsUL,
	StyledResultsSection,
	StyledViewAll,
	StyledParagraph,
} from './StyledComponents';
import { ImageWithPreviewPopover } from '../redesign/Results/ImageWithPreviewPopover';
import { HighlightedText } from '../redesign/HighlightedText';
import { getSpotlightResultClassName } from '../helpers/getSpotlightResultClassName';
import { useSearch } from '../SpotlightState';

interface Props {
	assetResults: AssetsSearchResults;
}

export function GroupAssets({ assetResults }: Props) {
	const { searchTerm, defaultSearchAction, addSearchTermToRecentSearches } = useSearch();
	const { translate } = useTranslate();
	const { canShowRecentSearches } = useSpotlightCapabilities();

	return (
		<StyledResultsSection>
			<ClassicNavigableHeadingButton onSelect={() => defaultSearchAction()} $noMarginTop>
				<div>{translate('spotlight.dropdown.assets')}</div>
				<StyledViewAll>
					<span>{translate('spotlight.dropdown.viewAll')}</span>
					<span> ({assetResults.total})</span>
				</StyledViewAll>
			</ClassicNavigableHeadingButton>

			<StyledItemsUL>
				{assetResults.items.map((asset) => {
					const displayName = asset.name;
					const extensionList = asset.extension?.join(', ').toUpperCase() || '';
					const matchExplanation = getMatchExplanation(asset, translate);

					return (
						<ClassicItem
							dataId={asset.id}
							dataSrc={asset.thumbnails?.large}
							afterSelect="close"
							onSelect={() =>
								onItemClick(asset.id, {
									highlight: asset.highlight,
									searchTerm,
									canShowRecentSearches,
									addSearchTermToRecentSearches,
								})
							}
							className={getSpotlightResultClassName('Asset')}
							key={asset.id}
						>
							<ImageWithPreviewPopover
								imageSrcMini={asset.thumbnails?.mini || ''}
								imageSrcLarge={asset.thumbnails?.large}
							/>

							<StyledInfoDiv>
								<StyledParagraph>{displayName}</StyledParagraph>

								{matchExplanation.length > 0 && (
									<div>
										<em>
											<HighlightedText segments={matchExplanation} />
										</em>
									</div>
								)}
							</StyledInfoDiv>
							{extensionList.length > 0 && <StyledBadge>{extensionList}</StyledBadge>}
						</ClassicItem>
					);
				})}
			</StyledItemsUL>
		</StyledResultsSection>
	);
}
